import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Filter, Inbox, Plus } from 'lucide-react';
import clsx from 'clsx';
import { useKeywordBatches, useMailAccountMutations, useMailAccounts } from '@/lib/queries';
import { useAuth } from '@/store/auth';
import { LogoMark } from '@/components/Logo';
import { MailboxForm, type MailboxFormValues } from '@/components/MailboxForm';
import { KeywordBatchForm } from '@/pages/KeywordsPage';
import { Alert, Button, Card, useToast } from '@/components/ui';

/**
 * First-run setup, in the order the system actually needs things.
 *
 * Mailboxes come first but do not start syncing — without keywords every
 * message matches, so checking now would ingest the whole lookback window
 * before the user has said what they care about. Saving the first keyword
 * batch is what starts the work.
 */
type Step = 'mailbox' | 'another' | 'keywords' | 'done';

const STEPS: Array<{ id: Step; label: string }> = [
  { id: 'mailbox', label: 'Connect a mailbox' },
  { id: 'keywords', label: 'Choose keywords' },
  { id: 'done', label: 'Start monitoring' },
];

function Stepper({ current }: { current: Step }) {
  // "another" is a branch of the mailbox step, not a step of its own.
  const activeId: Step = current === 'another' ? 'mailbox' : current;
  const activeIndex = STEPS.findIndex((s) => s.id === activeId);

  return (
    <ol className="mb-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs">
      {STEPS.map((step, index) => {
        const done = index < activeIndex;
        const active = index === activeIndex;
        return (
          <li key={step.id} className="flex items-center gap-2">
            <span
              className={clsx(
                'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold',
                done && 'bg-emerald-500/15 text-emerald-300',
                active && 'bg-brand-500/20 text-brand-200',
                !done && !active && 'bg-surface-overlay text-slate-600',
              )}
            >
              {done ? '✓' : index + 1}
            </span>
            <span className={clsx(active ? 'text-slate-200' : 'text-slate-600')}>{step.label}</span>
            {index < STEPS.length - 1 ? <span className="text-slate-700">—</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { data: accounts = [] } = useMailAccounts();
  const { data: batches = [] } = useKeywordBatches();
  const { create } = useMailAccountMutations();
  const { push } = useToast();

  const [step, setStep] = useState<Step>('mailbox');

  const addMailbox = async (values: MailboxFormValues) => {
    await create.mutateAsync({ ...values, username: values.username || values.emailAddress });
    // Adding the first mailbox is what completes onboarding server-side, so
    // the session has to be reloaded before the router will let us through.
    await refreshUser();
    push('success', `${values.label} connected. Nothing is checked yet — keywords come next.`);
    setStep('another');
  };

  const finish = async () => {
    await refreshUser();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <LogoMark className="mb-4 h-14 w-14" />
          <h1 className="text-2xl font-semibold tracking-tight text-slate-100">Set up your account</h1>
          <p className="mt-2 max-w-lg text-balance text-sm text-slate-500">
            Connect the mailboxes you want watched, then tell us which mail matters.
          </p>
        </div>

        <Stepper current={step} />

        {/* 1 — connect a mailbox */}
        {step === 'mailbox' ? (
          <Card className="p-6 sm:p-8">
            <h2 className="mb-1 text-lg font-semibold text-slate-100">Connect a mailbox</h2>
            <p className="mb-6 text-sm text-slate-500">
              Credentials are checked before they are saved, and stored encrypted. Nothing is read from the mailbox
              until you have set keywords.
            </p>
            <MailboxForm onSubmit={addMailbox} submitting={create.isPending} submitLabel="Connect mailbox" />
          </Card>
        ) : null}

        {/* 1b — add another, or move on */}
        {step === 'another' ? (
          <div className="space-y-5">
            <Alert severity="success" title={`${accounts.length} mailbox${accounts.length === 1 ? '' : 'es'} connected`}>
              Nothing is being checked yet. Keywords decide which mail is kept.
            </Alert>

            <Card>
              <ul className="divide-y divide-surface-border">
                {accounts.map((account) => (
                  <li key={account.id} className="flex items-center gap-3 px-5 py-3.5">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-200">{account.label}</p>
                      <p className="truncate text-xs text-slate-500">
                        {account.emailAddress} · from {new Date(account.syncFromDate).toLocaleDateString()}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6">
              <h2 className="text-base font-semibold text-slate-100">Add another mailbox?</h2>
              <p className="mt-1 text-sm text-slate-500">
                You can add more at any time from the Mailboxes screen — later ones automatically use whichever
                keyword batches are active then.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button variant="secondary" onClick={() => setStep('mailbox')} icon={<Plus className="h-4 w-4" />}>
                  Add another now
                </Button>
                <Button onClick={() => setStep('keywords')} icon={<ArrowRight className="h-4 w-4" />}>
                  Continue to keywords
                </Button>
              </div>
            </Card>
          </div>
        ) : null}

        {/* 2 — keywords */}
        {step === 'keywords' ? (
          <Card className="p-6 sm:p-8">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-100">
              <Filter className="h-4 w-4 text-brand-400" aria-hidden />
              Which mail matters?
            </h2>
            <p className="mb-6 text-sm text-slate-500">
              Only mail matching these keywords is kept and has its links opened. Everything else is counted and
              discarded. Separate terms with commas or <code className="text-slate-400">OR</code>.
            </p>
            <KeywordBatchForm
              hasExistingActive={batches.some((b) => b.isActive)}
              submitLabel="Save and start monitoring"
              onSaved={() => setStep('done')}
            />
          </Card>
        ) : null}

        {/* 3 — done */}
        {step === 'done' ? (
          <div className="space-y-5">
            <Alert severity="success" title="Monitoring has started">
              Your mailboxes are being checked now. Results appear on the dashboard within a few minutes, and new
              mail is picked up automatically as it arrives.
            </Alert>
            <div className="flex justify-end">
              <Button size="lg" onClick={finish} icon={<ArrowRight className="h-4 w-4" />}>
                Go to dashboard
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'mailbox' && accounts.length > 0 ? (
          <div className="mt-5 flex justify-center">
            <button
              onClick={() => setStep('another')}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-300"
            >
              <Inbox className="h-3.5 w-3.5" aria-hidden />
              Back to my {accounts.length} mailbox{accounts.length === 1 ? '' : 'es'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
